import { useCallback, useState } from "react";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import type { ObjectClassDefinition, ObjectFieldValuesDetails } from "../../api/types/objectTypes";
import useWorkflowActionChecks, { type WorkflowActionWithChecks } from "../approvals/workflows/useWorkflowActionChecks";
import type { ObjectCardPreviewChecks } from "./ObjectCardContext";
import { getActionCheckMessages, type ObjectCardPreviewConfig } from "./objectCardPreviewState";
import useObjectDuplicates from "./useObjectDuplicates";

export interface ObjectCardActionChecks extends WorkflowActionWithChecks {
  isOfferedByWorkflow: boolean;
}

const getWorkflowActionToCheck = (objectData: ObjectFieldValuesDetails, actionsWithChecks: WorkflowSignalType[]) => {
  for (const { workflowId, availableUserSignals, failureMessage } of objectData.workflowMetadata) {
    if (failureMessage) {
      continue;
    }

    const hasApproval = objectData.approvalMetadata.some((approval) => approval.workflowId === workflowId);
    const candidateActions: WorkflowSignalType[] = hasApproval
      ? [...availableUserSignals, "Approve"]
      : availableUserSignals;
    const action = candidateActions.find((signal) => actionsWithChecks.includes(signal));
    if (action) {
      return { workflowId, action, isOfferedByWorkflow: availableUserSignals.includes(action) };
    }
  }

  return undefined;
};

interface Params {
  objectType: string;
  objectId: string;
  objectData: ObjectFieldValuesDetails | undefined;
  objectDefinition: ObjectClassDefinition | undefined;
  cardPreview: ObjectCardPreviewConfig | undefined;
}

const useObjectCardChecks = ({ objectType, objectId, objectData, objectDefinition, cardPreview }: Params) => {
  const [previewChecks, setPreviewChecks] = useState<ObjectCardPreviewChecks>();

  const workflowActionToCheck =
    objectData && objectDefinition
      ? getWorkflowActionToCheck(objectData, objectDefinition.workflowActionsWithChecks ?? [])
      : undefined;
  const followsUnsavedEdits =
    cardPreview !== undefined && workflowActionToCheck?.isOfferedByWorkflow === true && previewChecks !== "unavailable";
  const savedActionChecks = useWorkflowActionChecks(followsUnsavedEdits ? undefined : workflowActionToCheck);

  const hasDuplicateDetection = objectDefinition?.duplicateDetection !== undefined;
  const duplicates = useObjectDuplicates(hasDuplicateDetection ? { objectType, objectId } : undefined);

  const { refetch: refetchActionChecks } = savedActionChecks;
  const { refetch: refetchDuplicates } = duplicates;
  const refetchChecks = useCallback(() => {
    void refetchActionChecks();
    void refetchDuplicates();
  }, [refetchActionChecks, refetchDuplicates]);

  const { blockers, warnings } = followsUnsavedEdits ? getActionCheckMessages(previewChecks ?? []) : savedActionChecks;

  const actionChecks: ObjectCardActionChecks | undefined = workflowActionToCheck && {
    ...workflowActionToCheck,
    blockers,
    warnings,
    hasFailed: savedActionChecks.hasFailed,
    isFetching: followsUnsavedEdits ? previewChecks === undefined : savedActionChecks.isFetching,
  };

  return {
    actionChecks,
    duplicates: hasDuplicateDetection ? { ...duplicates, objectType, objectId } : undefined,
    setPreviewChecks,
    refetchChecks,
  };
};

export default useObjectCardChecks;
